import { useState, useEffect, useMemo, useRef } from 'react'
import { Head } from '@inertiajs/react'
import { Select, Spin, Typography, Button, Modal } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { format, startOfWeek, endOfWeek, addDays, startOfDay, endOfDay, addWeeks, subWeeks, addMonths, subMonths } from 'date-fns'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import api from '../lib/api'

const { Title } = Typography
const { Option } = Select

const zhCnLocale = {
  code: 'zh-cn',
  week: { dow: 1, doy: 4 },
  buttonText: { prev: '上一个', next: '下一个', today: '今天', month: '月', week: '周', day: '日', list: '列表' },
  weekText: '周', allDayText: '全天', moreLinkText: '更多', noEventsText: '没有事件',
}

type Pomodoro = {
  id: string
  start_time: string
  end_time: string | null
  duration: number
  type: string
  completed: boolean
  status: string
  task_id: string | null
  task?: { id: string; title: string } | null
}

type ViewType = 'day' | 'week' | 'month'

const typeColors = { work: '#ff4d4f', break: '#52c41a' }

const initialViewType = (): ViewType => {
  if (typeof window === 'undefined') return 'month'
  return (new URLSearchParams(window.location.search).get('view_type') as ViewType) || 'month'
}

export default function Calendar() {
  const [pomodoros, setPomodoros] = useState<Pomodoro[]>([])
  const [loading, setLoading] = useState(true)
  const [viewType, setViewType] = useState<ViewType>(initialViewType)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const calendarRef = useRef<FullCalendar | null>(null)
  const [eventModalVisible, setEventModalVisible] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<{ title: string; type: string; startTime: string; duration: number; taskTitle?: string } | null>(null)

  // Keep view_type in the URL (native history, no full navigation).
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    if (params.get('view_type') !== viewType) {
      params.set('view_type', viewType)
      window.history.replaceState({}, '', `${window.location.pathname}?${params}`)
    }
  }, [viewType])

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi()
    if (calendarApi) {
      calendarApi.changeView(viewType === 'month' ? 'dayGridMonth' : viewType === 'week' ? 'timeGridWeek' : 'timeGridDay')
      calendarApi.gotoDate(selectedDate)
    }
  }, [viewType, selectedDate])

  useEffect(() => {
    ;(async () => {
      try {
        setLoading(true)
        setPomodoros(await api.pomodoros.getAll())
      } catch (error) {
        console.error('Error fetching pomodoros:', error)
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const formatDateRange = () => {
    switch (viewType) {
      case 'day':
        return format(selectedDate, 'yyyy年M月d日')
      case 'week': {
        const start = startOfWeek(selectedDate, { weekStartsOn: 1 })
        const end = endOfWeek(selectedDate, { weekStartsOn: 1 })
        return `${format(start, 'M月d日')} - ${format(end, 'M月d日')}`
      }
      case 'month':
        return format(selectedDate, 'yyyy年M月')
    }
  }

  const goToPrev = () => setSelectedDate((d) => (viewType === 'day' ? addDays(d, -1) : viewType === 'week' ? subWeeks(d, 1) : subMonths(d, 1)))
  const goToNext = () => setSelectedDate((d) => (viewType === 'day' ? addDays(d, 1) : viewType === 'week' ? addWeeks(d, 1) : addMonths(d, 1)))
  const goToToday = () => setSelectedDate(new Date())

  const fullCalendarEvents = useMemo(
    () =>
      pomodoros.map((p) => ({
        id: p.id,
        title: p.task?.title || (p.type === 'work' ? '专注时间' : '休息时间'),
        start: p.start_time,
        end: p.end_time || undefined,
        backgroundColor: typeColors[p.type as keyof typeof typeColors],
        extendedProps: { type: p.type, duration: p.duration, taskId: p.task_id, taskTitle: p.task?.title },
      })),
    [pomodoros],
  )

  return (
    <div className="calendar-container p-4 max-w-screen-2xl mx-auto">
      <Head title="日历" />
      <div className="calendar-header flex justify-between items-center mb-4">
        <div className="calendar-title flex items-center space-x-4">
          <Title level={2} style={{ margin: 0 }}>番茄钟日历</Title>
          <Button type="primary" onClick={goToToday}>今天</Button>
        </div>
        <div className="calendar-controls flex items-center space-x-4">
          <div className="calendar-nav flex items-center space-x-2">
            <Button icon={<LeftOutlined />} onClick={goToPrev} />
            <span className="text-lg font-medium">{formatDateRange()}</span>
            <Button icon={<RightOutlined />} onClick={goToNext} />
          </div>
          <Select value={viewType} onChange={(v: ViewType) => setViewType(v)} style={{ width: 120 }}>
            <Option value="day">日视图</Option>
            <Option value="week">周视图</Option>
            <Option value="month">月视图</Option>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-96"><Spin size="large" /></div>
      ) : (
        <div className="calendar-content">
          <div className="fullcalendar-container" style={{ height: '700px' }}>
            <FullCalendar
              key={viewType}
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={viewType === 'month' ? 'dayGridMonth' : viewType === 'week' ? 'timeGridWeek' : 'timeGridDay'}
              headerToolbar={false}
              initialDate={selectedDate}
              events={fullCalendarEvents}
              eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
              firstDay={1}
              allDaySlot={false}
              slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
              locale="zh-cn"
              locales={[zhCnLocale]}
              height="100%"
              dayCellDidMount={
                viewType === 'month'
                  ? (info) => {
                      const dayStart = startOfDay(info.date)
                      const dayEnd = endOfDay(info.date)
                      const dayPomodoros = pomodoros.filter((p) => {
                        const d = new Date(p.start_time)
                        return d >= dayStart && d <= dayEnd && p.completed
                      })
                      if (!dayPomodoros.length) return
                      const workCount = dayPomodoros.filter((p) => p.type === 'work').length
                      const breakCount = dayPomodoros.filter((p) => p.type === 'break').length
                      const container = document.createElement('div')
                      container.className = 'pomodoro-badge-container'
                      Object.assign(container.style, { position: 'absolute', top: '4px', right: '4px', display: 'flex' })
                      const makeBadge = (count: number, color: string, ml: string) => {
                        const b = document.createElement('div')
                        b.textContent = count > 99 ? '99+' : count.toString()
                        Object.assign(b.style, {
                          backgroundColor: color, color: 'white', borderRadius: '10px', padding: '0 6px',
                          fontSize: '12px', lineHeight: '20px', height: '20px', minWidth: '20px', textAlign: 'center', marginLeft: ml,
                        })
                        return b
                      }
                      if (workCount > 0) container.appendChild(makeBadge(workCount, typeColors.work, '0'))
                      if (breakCount > 0) container.appendChild(makeBadge(breakCount, typeColors.break, '4px'))
                      info.el.appendChild(container)
                    }
                  : undefined
              }
              dateClick={(info) => {
                setSelectedDate(info.date)
                if (viewType === 'month') setViewType('day')
              }}
              eventClick={(info) => {
                const e = info.event
                const props = e.extendedProps as { type: string; duration: number; taskId: string | null; taskTitle?: string }
                setSelectedEvent({
                  title: e.title,
                  type: props.type,
                  startTime: e.start ? format(new Date(e.start), 'HH:mm') : '',
                  duration: props.duration,
                  taskTitle: props.taskTitle,
                })
                setEventModalVisible(true)
              }}
            />
          </div>
        </div>
      )}

      <Modal
        title="番茄钟详情"
        open={eventModalVisible}
        onCancel={() => setEventModalVisible(false)}
        footer={[<Button key="close" onClick={() => setEventModalVisible(false)}>关闭</Button>]}
      >
        {selectedEvent && (
          <div>
            <p><strong>类型:</strong> {selectedEvent.type === 'work' ? '专注时间' : '休息时间'}</p>
            <p><strong>开始时间:</strong> {selectedEvent.startTime}</p>
            <p><strong>持续时间:</strong> {selectedEvent.duration} 分钟</p>
            {selectedEvent.taskTitle && <p><strong>关联任务:</strong> {selectedEvent.taskTitle}</p>}
          </div>
        )}
      </Modal>
    </div>
  )
}
